import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const ResearchOverview: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const researchAreas = [
    {
      title: 'Neural Circuits & Consciousness',
      description: 'Investigating how neural circuits mediate consciousness, mood regulation, and pain perception through advanced imaging and manipulation techniques.',
    },
    {
      title: 'Mood & Depression',
      description: 'Exploring circuit mechanisms underlying rapid-acting antidepressants and psychedelics to develop novel therapeutic strategies for mood disorders.',
    },
    {
      title: 'Pain Mechanisms',
      description: 'Mapping neural circuits that process and modulate pain, with focus on understanding chronic pain transitions and developing targeted interventions.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section
      ref={ref}
      id="research"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-penn-blue mb-6">
            Our Research
          </h2>
          <div className="w-24 h-1 bg-penn-red mx-auto mb-8"></div>
          <p className="text-lg md:text-xl text-neutral-dark max-w-4xl mx-auto leading-relaxed mb-6">
            The focus of our research is on combining two-photon imaging with opto/chemogenetics
            to map layer- and cell-type specific cortical dynamics and behavior to understand how
            anesthetics, rapid-acting antidepressants, and psychedelics reshape circuits to alter
            consciousness, mood, and pain.
          </p>
          <p className="text-base md:text-lg text-neutral-dark max-w-4xl mx-auto leading-relaxed">
            The broader goal is to translate mechanistic circuit insights into new strategies for
            treating depression, chronic pain, and disorders of arousal.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {researchAreas.map((area, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="bg-neutral-light rounded-lg p-8 shadow-sm hover:shadow-xl transition-shadow duration-300 border border-neutral-medium hover:border-penn-red/30"
            >
              <h3 className="text-2xl font-bold text-penn-blue mb-4">
                {area.title}
              </h3>
              <p className="text-neutral-dark leading-relaxed">
                {area.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ResearchOverview;

